import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { replyToTicket } from "../api/ticketsApi";
import { Ticket } from "../types";
import { Loader2 } from "lucide-react";

const formSchema = z.object({
  reply: z.string().min(1, "Reply message is required"),
});

type FormValues = z.infer<typeof formSchema>;

interface ReplyTicketDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticket: Ticket | null;
  userMap?: Map<number, string>;
}

export default function ReplyTicketDialog({ open, onOpenChange, ticket, userMap }: ReplyTicketDialogProps) {
  const queryClient = useQueryClient();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { reply: "" },
  });

  const replyMutation = useMutation({
    mutationFn: (values: FormValues) => replyToTicket(ticket!.id, { admin_reply: values.reply }),

    onSuccess: () => {
      toast.success("Reply sent successfully");
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
      onOpenChange(false);
    },
    onError: () => {
      toast.error("Failed to send reply");
    },
  });

  function handleSubmit(values: FormValues) {
    replyMutation.mutate(values);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-[95vw] sm:max-w-[550px] p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Reply to Ticket</DialogTitle>
          <DialogDescription>
            {ticket ? (userMap?.get(ticket.user_id) ?? `User #${ticket.user_id}`) : ""}
            {ticket?.subject ? ` — ${ticket.subject}` : ""}
          </DialogDescription>
        </DialogHeader>

        {ticket && (
          <div className="space-y-2">
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Description:</p>
              <p>{ticket.description}</p>
            </div>
            {ticket.file_url && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-foreground">Attachment:</p>
                <a href={ticket.file_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={ticket.file_url}
                    alt="Ticket attachment"
                    className="max-h-48 sm:max-h-64 w-full rounded-md border object-contain cursor-pointer hover:opacity-90 transition-opacity"
                  />
                </a>
              </div>
            )}
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reply"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reply</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Type your reply here..."
                      rows={5}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={replyMutation.isPending}>
                {replyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reply
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
